/**
 * @Description: 客户留言(Customer Message)与客户回访(Return Visit)相关接口
 * @Doc: 客户留言与回访接口对接文档.md
 * 基础路径：/sphere-admin/api/v1/sphere/customer/messages （客户留言）
 *          /sphere-admin/api/v1/sphere/return/visits      （客户回访）
 */
import request from './clientRequest'

const MESSAGE_BASE = '/sphere-admin/api/v1/sphere/customer/messages'
const VISIT_BASE = '/sphere-admin/api/v1/sphere/return/visits'

/**
 * 2.1 新增留言
 * @param {Object} data 留言参数
 * @param {String} data.customerName 客户姓名（必填）
 * @param {String} [data.position] 职位
 * @param {String} [data.companyName] 公司名称
 * @param {String} [data.phone] 联系电话
 * @param {String} [data.email] 电子邮箱
 * @param {Number} [data.channel] 渠道，1-官网 2-搜索引擎 3-同事推荐 4-行业活动 5-社交媒体 6-其他
 * @param {String} [data.demandDesc] 需求说明
 * @param {Number} [data.source] 留言来源，1-产品介绍页 2-数字人广场 4-数字人介绍页
 * @param {String} [data.tags] 标签 id 逗号分隔
 * @returns {Promise} data 为完整留言对象
 */
export function addCustomerMessage(data) {
  return request({
    url: `${MESSAGE_BASE}/add`,
    method: 'post',
    data
  })
}

/**
 * 2.3 留言详情（附带全部回访记录 returnVisits）
 * @param {Number|String} id 留言 id
 * @returns {Promise} data 为留言对象（含 returnVisits）
 */
export function getCustomerMessage(id) {
  return request({
    url: `${MESSAGE_BASE}/${id}`,
    method: 'get'
  })
}

/**
 * 2.4 分页查询留言
 * @param {Object} params 查询参数
 * @param {Number} [params.status] 回访状态，0-未回访 1-已回访
 * @param {String} [params.customerName] 客户姓名（模糊）
 * @param {String} [params.companyName] 公司名称（模糊）
 * @param {String} [params.phone] 联系电话（模糊）
 * @param {String} [params.email] 电子邮箱（模糊）
 * @param {Number} [params.source] 留言来源，1-产品介绍页 2-数字人广场 3-套餐详情 4-数字人介绍 5-部署实施 6-资源耗尽
 * @param {String} [params.tag] 标签（模糊）
 * @param {Number} [params.iamUserId] IAM 用户 id，精确匹配
 * @param {Number} [params.page] 页码，默认 1
 * @param {Number} [params.size] 每页大小，默认 20，最大 200
 * @returns {Promise} data 为分页结构 { records, total, size, current, pages }
 */
export function pageCustomerMessages(params) {
  return request({
    url: `${MESSAGE_BASE}/page`,
    method: 'get',
    params
  })
}

/**
 * 2.5 编辑留言（稀疏更新）
 * @param {Number|String} id 留言 id
 * @param {Object} data 待更新字段
 * @returns {Promise} data 为更新后的留言对象
 */
export function updateCustomerMessage(id, data) {
  return request({
    url: `${MESSAGE_BASE}/update/${id}`,
    method: 'post',
    data
  })
}

/**
 * 2.6 删除留言（逻辑删除）
 * @param {Number|String} id 留言 id
 * @returns {Promise} data 为 null
 */
export function deleteCustomerMessage(id) {
  return request({
    url: `${MESSAGE_BASE}/delete/${id}`,
    method: 'post'
  })
}

/**
 * 2.7 标记为已回访
 * @param {Number|String} id 留言 id
 * @returns {Promise} data 为更新后的留言对象
 */
export function markMessageVisited(id) {
  return request({
    url: `${MESSAGE_BASE}/${id}/mark-visited`,
    method: 'post'
  })
}

/**
 * 3.1 新增回访
 * @param {Object} data 回访参数
 * @param {Number} data.messageId 关联留言 id（必填）
 * @param {Number} data.returnVisitMethod 回访方式，1-电话 2-邮件 3-线上会议 4-上门拜访
 * @param {String} data.returnVisitMessage 回访沟通内容（必填）
 * @param {String} [data.returnVisitTime] 回访时间 yyyy-MM-dd
 * @param {String} [data.tags] 留言标签 id 逗号分隔
 * @returns {Promise} data 为回访对象
 */
export function addReturnVisit(data) {
  return request({
    url: `${VISIT_BASE}/add`,
    method: 'post',
    data
  })
}

/**
 * 3.3 回访详情
 * @param {Number|String} id 回访记录 id
 * @returns {Promise} data 为回访对象
 */
export function getReturnVisit(id) {
  return request({
    url: `${VISIT_BASE}/${id}`,
    method: 'get'
  })
}

/**
 * 3.4 分页查询回访
 * @param {Object} params 查询参数
 * @param {Number} [params.messageId] 留言 id，精确匹配
 * @param {Number} [params.returnVisitMethod] 回访方式，精确匹配
 * @param {Number} [params.page] 页码，默认 1
 * @param {Number} [params.size] 每页大小，默认 20，最大 200
 * @returns {Promise} data 为分页结构
 */
export function pageReturnVisits(params) {
  return request({
    url: `${VISIT_BASE}/page`,
    method: 'get',
    params
  })
}

/**
 * 3.5 编辑回访（稀疏更新）
 * @param {Number|String} id 回访记录 id
 * @param {Object} data 待更新字段
 * @returns {Promise} data 为更新后的回访对象
 */
export function updateReturnVisit(id, data) {
  return request({
    url: `${VISIT_BASE}/update/${id}`,
    method: 'post',
    data
  })
}

/**
 * 3.6 删除回访（逻辑删除）
 * @param {Number|String} id 回访记录 id
 * @returns {Promise} data 为 null
 */
export function deleteReturnVisit(id) {
  return request({
    url: `${VISIT_BASE}/delete/${id}`,
    method: 'post'
  })
}
